import { UserSecurityApiClient } from '@fourthwall/services/dist/UserSecurityApiClient'

import { type IdentityProvider } from '../Security.types'
import type { SecurityViewProps } from '../SecurityView'
import { useDeleteUserSecuritySessionMutation } from '../queries/useDeleteUserSecuritySessionMutation'
import { useUserSecurityQuery } from '../queries/useUserSecurityQuery'
import { isIdentityProvider } from '../utils/isIdentityProvider'

export const CREDENTIAL_TYPES = {
  passkey: 'webauthn',
  authenticator: 'otp',
}

export const useSecurity = () => {
  const { userSecurityQuery, invalidateUserSecurityQuery } = useUserSecurityQuery()
  const { deleteUserSecuritySessionMutation } = useDeleteUserSecuritySessionMutation()

  const password = ((): SecurityViewProps['password'] => {
    if (!userSecurityQuery.isSuccess) return null

    const { credentials } = userSecurityQuery.data

    const passwordCredential = credentials.find((credential) => credential.type === 'password')

    return {
      createdDate: passwordCredential ? passwordCredential.createdDate : null,
      updatePasswordUrl: UserSecurityApiClient.getUserSecurityRedirectUrl({
        action: 'UPDATE_PASSWORD',
      }),
    }
  })()

  const identityProviders = ((): {
    provider: IdentityProvider
    username: string
  }[] => {
    if (!userSecurityQuery.isSuccess) return []

    const { identityProviders } = userSecurityQuery.data

    if (!identityProviders.length) return []

    return identityProviders
      .filter((item): item is typeof item & { identityProvider: IdentityProvider } =>
        isIdentityProvider(item.identityProvider),
      ) // 🙄
      .map((item) => ({
        provider: item.identityProvider,
        username: item.userName,
      }))
  })()

  const loginPlatforms = ((): SecurityViewProps['loginPlatforms'] => {
    return identityProviders.length ? { items: identityProviders } : null
  })()

  const deleteCredential = (id: string) => {
    const url = UserSecurityApiClient.getUserSecurityRedirectUrl({
      action: 'fw_delete_credential',
      id,
    })

    window.location.assign(url)
  }

  const deleteDevice = (id: string) => {
    deleteUserSecuritySessionMutation.mutate([{ sessionId: id }], {
      onSuccess: () => {
        invalidateUserSecurityQuery()
      },
    })
  }

  const passkeys = ((): SecurityViewProps['passkeys'] => {
    if (!userSecurityQuery.isSuccess) return null

    const { credentials } = userSecurityQuery.data

    return {
      credentials: credentials
        .filter((credential) => credential.type === CREDENTIAL_TYPES.passkey)
        .map((credential) => ({ ...credential, name: credential.userLabel || 'Unknown passkey' })),
      setUpUrl: UserSecurityApiClient.getUserSecurityRedirectUrl({ action: 'webauthn-register' }),
      onDelete: deleteCredential,
    }
  })()

  const authenticators = ((): SecurityViewProps['authenticators'] => {
    if (!userSecurityQuery.isSuccess) return null

    const { credentials } = userSecurityQuery.data

    return {
      credentials: credentials
        .filter((credential) => credential.type === CREDENTIAL_TYPES.authenticator)
        .map((credential) => ({
          ...credential,
          name: credential.userLabel || 'Unknown authenticator',
        })),
      setUpUrl: UserSecurityApiClient.getUserSecurityRedirectUrl({ action: 'CONFIGURE_TOTP' }),
      onDelete: deleteCredential,
    }
  })()

  const devices = ((): SecurityViewProps['devices'] => {
    if (!userSecurityQuery.isSuccess) return null

    const { sessions } = userSecurityQuery.data

    return {
      items: sessions.map((session) => ({
        id: session.id,
        name: session.name,
        lastActive: session.lastAccess,
        address: session.ipAddress,
        isActive: session.current,
        type: session.mobile ? 'MOBILE' : 'DESKTOP',
        onDelete: !session.current ? () => deleteDevice(session.id) : undefined,
      })),
    }
  })()

  return {
    password,
    loginPlatforms,
    passkeys,
    authenticators,
    devices,
  }
}
